import {ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';


import {TableModule} from 'primeng/table';
import {Loader} from '../../../../components/loader/loader';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {DurationPipe} from '../../../timesheets/pipes/duration.pipe';
import {TimesheetStateBadge} from '../../../../components/timesheet-state-badge/timesheet-state-badge';
import {QueryKey} from '../../../../constants/query-key';
import {BillingTimesheetState, TimesheetService, TimesheetSummary} from '../../../../../api';

@Component({
  selector: 'elcia-invoice-timesheet-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ElciaDialog, Loader, TableModule, TranslatePipe, DatePipe, DurationPipe, TimesheetStateBadge],
  templateUrl: './invoice-timesheet-picker.html',
  styleUrl: './invoice-timesheet-picker.scss',
})
export class InvoiceTimesheetPicker {
  private readonly timesheetService = inject(TimesheetService);

  readonly timesheetTypeId = input.required<number>();
  readonly customerId = input.required<number>();
  readonly initialSelectedIds = input<number[]>([]);

  readonly confirmed = output<TimesheetSummary[]>();
  readonly onClose = output();

  private readonly startDate = new Date('2020-01-01');
  private readonly endDate = new Date();

  private readonly timesheetsQuery = injectQuery(() => ({
    queryKey: [QueryKey.TIMESHEETS, this.customerId(), this.timesheetTypeId()],
    queryFn: () => lastValueFrom(
      this.timesheetService.getTimesheets(
        [this.customerId()],
        [],
        [this.timesheetTypeId()],
        [],
        [],
        null,
        true,
        [BillingTimesheetState.VALID, BillingTimesheetState.LINKEDTOINVOICE],
        this.startDate,
        this.endDate
      ),
    ),
  }));

  protected readonly isLoading = computed(() => this.timesheetsQuery.isPending());

  // L'API ne renvoie déjà que les états VALID et LINKED_TO_INVOICE.
  protected readonly availableTimesheets = computed<TimesheetSummary[]>(() =>
    this.timesheetsQuery.data()?.timesheets ?? [],
  );

  // Une prestation rattachée à une facture est verrouillée, sauf si elle appartient à la ligne éditée
  // (auquel cas on doit pouvoir la retirer). Couvre aussi bien les lignes sœurs que les autres factures.
  protected readonly disabledIds = computed<Set<number>>(() => {
    const keepIds = new Set(this.initialSelectedIds());
    return new Set(
      this.availableTimesheets()
        .filter(t => t.billingTimesheetState === BillingTimesheetState.LINKEDTOINVOICE && !keepIds.has(t.id))
        .map(t => t.id),
    );
  });

  protected isDisabled(ts: TimesheetSummary): boolean {
    return this.disabledIds().has(ts.id);
  }

  protected readonly selectedTimesheets = linkedSignal<TimesheetSummary[]>(() => {
    const initialIds = new Set(this.initialSelectedIds());
    return this.availableTimesheets().filter(t => initialIds.has(t.id));
  });

  // Neutralise le « tout sélectionner » de l'en-tête : on ignore toute prestation verrouillée.
  protected onSelectionChange(timesheets: TimesheetSummary[]): void {
    this.selectedTimesheets.set(timesheets.filter(t => !this.isDisabled(t)));
  }

  protected confirm(): void {
    this.confirmed.emit(this.selectedTimesheets());
    this.onClose.emit();
  }
}
